import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/Header';
import { colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import { useFirmalar } from '../../hooks/useFirmalar';
import { parseTutarTR, formatTutarTR } from '../../utils/para';
import extractApiError from '../../utils/apiError';
import { logError } from '../../utils/logError';

// #2b Geçici Vergi — firma + çeyrek dönem bazlı ödenen geçici (advance) vergi takibi.
// Backend /api/gecici-vergi (CRUD, role-guarded: muhasebe/yonetim/admin).
// Karşılaştırma bandı: seçilen yıl için ödenen geçici vergi vs tahmini kurumlar vergisi (#2a).

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtTarih = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString('tr-TR');
};

const DONEM_OPTIONS = [1, 2, 3, 4];
const currentYear = new Date().getFullYear();

const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#555',
};
const tdStyle = { padding: '8px 12px', fontSize: '14px', verticalAlign: 'middle' };

const emptyForm = () => ({
  id: null,
  firma: '',
  donem_yil: String(currentYear),
  donem_no: '1',
  tutar: '',
  odeme_tarihi: new Date().toISOString().split('T')[0],
  beyan_tarihi: '',
  notlar: '',
});

const GeciciVergiPage = () => {
  const toast = useToast();
  const { data: firmalarData } = useFirmalar();
  // Geçici vergi tüzel kişi başına beyan/ödenir → yalnız SIRKET firmalar (OFIS gibi
  // masraf merkezleri hariç; hatırlatma servisi de tip=="SIRKET" filtreler — hizalı).
  const firmalar = useMemo(
    () => (Array.isArray(firmalarData)
      ? firmalarData.filter((f) => f.aktif !== false && (f.tip || "SIRKET") === "SIRKET")
      : []),
    [firmalarData]
  );

  const [items, setItems] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [loading, setLoading] = useState(true);

  // Tahmini kurumlar vergisi (#2a) — cockpit'ten seçilen yıl için
  const [tahminKurumlar, setTahminKurumlar] = useState(null);

  // Filtreler — URL'e bağlı (navigasyon + refresh sonrası yaşar, link paylaşılabilir).
  const [yil, setYil] = useUrlState('gv_yil', String(currentYear));
  const [firma, setFirma] = useUrlState('gv_firma', '');
  const [includeInactive, setIncludeInactive] = useUrlState('gv_pasif', '');

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const getFirmaLabel = useCallback(
    (kod) => firmalar.find((f) => f.kod === kod)?.ad || kod || '-',
    [firmalar]
  );

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (firma) params.set('firma', firma);
      if (yil) params.set('yil', yil);
      if (includeInactive === '1') params.set('include_inactive', 'true');
      const res = await fetch(`${API}/api/gecici-vergi?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      setItems(Array.isArray(data.items) ? data.items : []);
      setToplam(data.toplam ?? 0);
    } catch (err) {
      logError('Geçici vergi listesi yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [firma, yil, includeInactive, toast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Tahmini kurumlar vergisi — seçilen yıl için cockpit'ten. Hata/eksikse sessizce gizle.
  useEffect(() => {
    let iptal = false;
    const yilNum = parseInt(yil, 10);
    if (!yilNum) { setTahminKurumlar(null); return undefined; }
    (async () => {
      try {
        const params = new URLSearchParams({ baslangic: `${yilNum}-01-01`, bitis: `${yilNum}-12-31` });
        const res = await fetch(`${API}/api/reports/executive-cockpit?${params.toString()}`, { headers: authHeaders() });
        if (!res.ok) { if (!iptal) setTahminKurumlar(null); return; }
        const data = await res.json();
        const kv = data?.pnl_compact?.kurumlar_vergisi_tl;
        if (!iptal) setTahminKurumlar(typeof kv === 'number' && kv > 0 ? kv : null);
      } catch {
        // Banttaki tahmin parçası tamamen opsiyonel — sayfayı asla çökertme.
        if (!iptal) setTahminKurumlar(null);
      }
    })();
    return () => { iptal = true; };
  }, [yil]);

  const openYeni = () => { setForm(emptyForm()); setModalOpen(true); };

  const openDuzenle = (it) => {
    setForm({
      id: it.id,
      firma: it.firma || '',
      donem_yil: String(it.donem_yil || currentYear),
      donem_no: String(it.donem_no || '1'),
      tutar: formatTutarTR(it.tutar),
      odeme_tarihi: it.odeme_tarihi ? it.odeme_tarihi.split('T')[0] : '',
      beyan_tarihi: it.beyan_tarihi ? it.beyan_tarihi.split('T')[0] : '',
      notlar: it.notlar || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => { if (!saving) setModalOpen(false); };

  const handleSave = async () => {
    if (!form.firma) { toast.error('Firma seçiniz'); return; }
    const tutarNum = parseTutarTR(form.tutar);
    if (!tutarNum || tutarNum <= 0) { toast.error('Geçerli bir tutar giriniz'); return; }
    if (!form.odeme_tarihi) { toast.error('Ödeme tarihi giriniz'); return; }

    setSaving(true);
    try {
      const isEdit = !!form.id;
      const body = {
        firma: form.firma,
        donem_yil: parseInt(form.donem_yil, 10),
        donem_no: parseInt(form.donem_no, 10),
        tutar: tutarNum,
        odeme_tarihi: form.odeme_tarihi,
        beyan_tarihi: form.beyan_tarihi || null,
        notlar: form.notlar.trim() || null,
      };
      const res = await fetch(
        isEdit ? `${API}/api/gecici-vergi/${form.id}` : `${API}/api/gecici-vergi`,
        {
          method: isEdit ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();
      if (!res.ok) { toast.error(extractApiError(data.detail)); return; }
      toast.success(isEdit ? 'Geçici vergi güncellendi' : 'Geçici vergi kaydedildi');
      setModalOpen(false);
      fetchItems();
    } catch (err) {
      logError('Geçici vergi kaydetme hatası:', err);
      toast.error('Sunucu hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasiflestir = async (it) => {
    if (!window.confirm(`${getFirmaLabel(it.firma)} — ${it.donem_yil}/${it.donem_no}. Dönem kaydını pasifleştirmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`${API}/api/gecici-vergi/${it.id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(extractApiError(data.detail)); return; }
      toast.success('Kayıt pasifleştirildi');
      fetchItems();
    } catch (err) {
      logError('Geçici vergi pasifleştirme hatası:', err);
      toast.error('Sunucu hatası: ' + err.message);
    }
  };

  const handleAktiflestir = async (it) => {
    try {
      const res = await fetch(`${API}/api/gecici-vergi/${it.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ aktif: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(extractApiError(data.detail)); return; }
      toast.success('Kayıt aktifleştirildi');
      fetchItems();
    } catch (err) {
      logError('Geçici vergi aktifleştirme hatası:', err);
      toast.error('Sunucu hatası: ' + err.message);
    }
  };

  const handleSil = async (it) => {
    if (!window.confirm(`${getFirmaLabel(it.firma)} — ${it.donem_yil}/${it.donem_no}. Dönem kaydı KALICI olarak silinecek. Devam edilsin mi?`)) return;
    try {
      const res = await fetch(`${API}/api/gecici-vergi/${it.id}?hard=true`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(extractApiError(data.detail)); return; }
      toast.success('Kayıt kalıcı olarak silindi');
      fetchItems();
    } catch (err) {
      logError('Geçici vergi silme hatası:', err);
      toast.error('Sunucu hatası: ' + err.message);
    }
  };

  const odenen = toplam;
  const kalan = tahminKurumlar != null ? Math.max(0, tahminKurumlar - odenen) : null;

  if (loading && items.length === 0) {
    return (
      <div>
        <Header title="Geçici Vergi" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Geçici Vergi" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Üst bar: başlık + filtreler + Ekle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>🧾 Geçici Vergi</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Yıl</label>
              <input type="number" value={yil} onChange={(e) => setYil(e.target.value)}
                style={{ ...inputStyle, width: '110px' }} />
            </div>
            <div style={{ minWidth: '180px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Firma</label>
              <select value={firma} onChange={(e) => setFirma(e.target.value)} style={inputStyle}>
                <option value="">Tümü</option>
                {firmalar.map((f) => (<option key={f.kod} value={f.kod}>{f.ad}</option>))}
              </select>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary, cursor: 'pointer', padding: '8px 0' }}>
              <input type="checkbox" checked={includeInactive === '1'}
                onChange={(e) => setIncludeInactive(e.target.checked ? '1' : '')} />
              Pasifleri göster
            </label>
            <button onClick={openYeni} style={{
              padding: '9px 18px', border: 'none', borderRadius: radius.sm, background: colors.primary,
              color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
            }}>+ Ekle</button>
          </div>
        </div>

        {/* Karşılaştırma bandı: ödenen vs tahmini kurumlar vergisi vs kalan */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '24px',
        }}>
          <div style={{ background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${colors.primary}`, padding: '16px', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
              {yil} — Ödenen Geçici Vergi
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
              {formatTutarTR(odenen)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
            </div>
          </div>
          {tahminKurumlar != null && (
            <>
              <div style={{ background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${colors.warning}`, padding: '16px', boxShadow: shadows.sm }}>
                <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  Tahmini Kurumlar Vergisi (#2a)
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                  {formatTutarTR(tahminKurumlar)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
                </div>
              </div>
              <div style={{ background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${kalan > 0 ? colors.danger : colors.success}`, padding: '16px', boxShadow: shadows.sm }}>
                <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  Kalan (Tahmin − Ödenen)
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: kalan > 0 ? colors.danger : colors.success }}>
                  {formatTutarTR(kalan)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'hidden' }}>
          {items.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
              Bu filtreye uyan geçici vergi kaydı bulunmuyor.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt }}>
                    {['Dönem', 'Firma', 'Tutar', 'Ödeme Tarihi', 'Beyan Tarihi', 'Notlar', 'İşlemler'].map((h) => (
                      <th key={h} style={{
                        padding: '10px 12px', fontSize: '11px', fontWeight: 600, color: colors.textTertiary,
                        letterSpacing: '0.04em', textAlign: h === 'Tutar' ? 'right' : 'left',
                        whiteSpace: 'nowrap', borderBottom: `2px solid ${colors.border}`,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const pasif = it.aktif === false;
                    return (
                      <tr key={it.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: pasif ? 0.55 : 1 }}>
                        <td style={{ ...tdStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{it.donem_yil}/{it.donem_no}. Dönem</td>
                        <td style={tdStyle}>{getFirmaLabel(it.firma)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {formatTutarTR(it.tutar)} ₺
                        </td>
                        <td style={tdStyle}>{fmtTarih(it.odeme_tarihi)}</td>
                        <td style={tdStyle}>{fmtTarih(it.beyan_tarihi)}</td>
                        <td style={{ ...tdStyle, color: colors.textSecondary, fontSize: '13px' }}>{it.notlar || '-'}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                          <button onClick={() => openDuzenle(it)} title="Düzenle" style={{
                            background: '#1565c0', color: 'white', border: 'none', borderRadius: radius.sm,
                            padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', marginRight: '6px',
                          }}>Düzenle</button>
                          {pasif ? (
                            <button onClick={() => handleAktiflestir(it)} title="Aktifleştir" style={{
                              background: colors.success, color: 'white', border: 'none', borderRadius: radius.sm,
                              padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', marginRight: '6px',
                            }}>Aktifleştir</button>
                          ) : (
                            <button onClick={() => handlePasiflestir(it)} title="Pasifleştir" style={{
                              background: '#f59e0b', color: 'white', border: 'none', borderRadius: radius.sm,
                              padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', marginRight: '6px',
                            }}>Pasifleştir</button>
                          )}
                          <button onClick={() => handleSil(it)} title="Kalıcı sil" style={{
                            background: '#e53935', color: 'white', border: 'none', borderRadius: radius.sm,
                            padding: '6px 10px', cursor: 'pointer', fontSize: '12px',
                          }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: colors.backgroundAlt, borderTop: `2px solid ${colors.border}` }}>
                    <td colSpan={2} style={{ padding: '10px 12px', fontWeight: 700, fontSize: '13px' }}>
                      Toplam (aktif, {items.filter((x) => x.aktif !== false).length} kayıt)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: '14px', fontVariantNumeric: 'tabular-nums' }}>
                      {formatTutarTR(toplam)} ₺
                    </td>
                    <td colSpan={4} style={{ padding: '10px 12px' }}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Modal: Ekle / Düzenle */}
        {modalOpen && (
          <div
            onClick={closeModal}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.lg,
                width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', padding: '24px',
              }}
            >
              <h3 style={{ margin: '0 0 18px', color: colors.textPrimary }}>
                {form.id ? 'Geçici Vergi Düzenle' : 'Yeni Geçici Vergi'}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Firma *</label>
                  <select value={form.firma} onChange={(e) => setForm({ ...form, firma: e.target.value })} style={inputStyle}>
                    <option value="">Firma Seçiniz</option>
                    {firmalar.map((f) => (<option key={f.kod} value={f.kod}>{f.ad}</option>))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Yıl *</label>
                  <input type="number" value={form.donem_yil}
                    onChange={(e) => setForm({ ...form, donem_yil: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Dönem *</label>
                  <select value={form.donem_no} onChange={(e) => setForm({ ...form, donem_no: e.target.value })} style={inputStyle}>
                    {DONEM_OPTIONS.map((n) => (<option key={n} value={String(n)}>{n}. Dönem</option>))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Tutar (₺) *</label>
                  <input type="text" inputMode="decimal" value={form.tutar}
                    onChange={(e) => setForm({ ...form, tutar: e.target.value })}
                    placeholder="0,00" style={{ ...inputStyle, textAlign: 'right', fontWeight: 600 }} />
                </div>
                <div>
                  <label style={labelStyle}>Ödeme Tarihi *</label>
                  <input type="date" value={form.odeme_tarihi}
                    onChange={(e) => setForm({ ...form, odeme_tarihi: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Beyan Tarihi</label>
                  <input type="date" value={form.beyan_tarihi}
                    onChange={(e) => setForm({ ...form, beyan_tarihi: e.target.value })} style={inputStyle} />
                </div>
              </div>

              <div style={{ marginTop: '14px' }}>
                <label style={labelStyle}>Notlar</label>
                <input type="text" value={form.notlar}
                  onChange={(e) => setForm({ ...form, notlar: e.target.value })}
                  placeholder="Ek açıklama (opsiyonel)…" style={inputStyle} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
                <button onClick={closeModal} disabled={saving} style={{
                  padding: '9px 18px', background: 'transparent', color: colors.textSecondary,
                  border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                  cursor: saving ? 'default' : 'pointer', fontSize: '13px', fontWeight: 600,
                }}>İptal</button>
                <button onClick={handleSave} disabled={saving} style={{
                  padding: '9px 24px', background: saving ? '#ccc' : colors.primary, color: 'white',
                  border: 'none', borderRadius: radius.sm, cursor: saving ? 'default' : 'pointer',
                  fontSize: '14px', fontWeight: 'bold',
                }}>{saving ? 'Kaydediliyor…' : (form.id ? 'Güncelle' : 'Kaydet')}</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default GeciciVergiPage;
